import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Upload, Wand2, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import ScriptRevisionPanel from "./ScriptRevisionPanel";
import ConfirmReplaceScriptDialog from "./ConfirmReplaceScriptDialog";
import ScriptIndexingRecovery from "./ScriptIndexingRecovery";
import ScriptParsedBadge from "./ScriptParsedBadge";
import ConfirmRebuildSceneCastDialog from "./ConfirmRebuildSceneCastDialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";

/**
 * iter-51 Episodic TV Script Setup.
 *
 * Renders a list of episode "slots", each with its own upload + parse flow.
 * The owner first creates a slot (e.g. Episode "1" / "Pilot"), then uploads
 * a PDF/DOCX/TXT, then taps Parse to run scene extraction scoped to that
 * episode's draft scenes only. Re-parsing one episode does not blow away
 * any other episode's scenes (handled server-side via episode-scoped
 * draft_query in /scripts/{id}/parse).
 *
 * Scope: This component is mounted by ProjectSettings only when the project
 * type is `tv_series`. Feature Film and Single TV Episode projects keep
 * using the legacy single-script ScriptSection unchanged.
 */
export default function EpisodicScriptSetup({ projectId }) {
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [scripts, setScripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [addOpen, setAddOpen] = useState(false);
    const [newEpisode, setNewEpisode] = useState("");
    const [newTitle, setNewTitle] = useState("");
    const [submittingNew, setSubmittingNew] = useState(false);
    const [newError, setNewError] = useState("");

    const [busyId, setBusyId] = useState(null); // upload/parse/delete in-flight
    const [recoveryBusyId, setRecoveryBusyId] = useState(null); // {id, kind}
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    // Iter-141 — local fallback for indexing-start when the server
    // didn't stamp it (legacy docs). Keyed by script id; cleared
    // automatically when the doc leaves the indexing state.
    const [localIndexingStart, setLocalIndexingStart] = useState({});
    // Iter-61: per-script replacement confirmation. We hold the picked
    // file (and its parent script) until the user confirms the overwrite.
    const [pendingReplace, setPendingReplace] = useState(null);
    // Iter-155B.3 — Replace confirmation BEFORE the OS file picker
    // opens. Holds the script slot the user clicked Replace on.
    const [pendingReplaceIntent, setPendingReplaceIntent] = useState(null);
    // shape: { script, file }
    // Iter-147 — confirmation gate before re-running Build Scenes &
    // Cast on a slot that's already been parsed. First parse goes
    // straight through; only re-parses pass through the dialog.
    const [pendingRebuild, setPendingRebuild] = useState(null);
    // shape: { script } | null

    // Iter-157P.19 — persistent extraction-warning state. The
    // previous implementation surfaced the zero-scene parse error
    // as a sonner toast, which dismissed itself within seconds and
    // left the producer with no actionable trace. We now mirror the
    // ScriptSection / PrepMode pattern: hold the structured
    // payload (active_format, script id) in component state and
    // render a persistent calm red panel inline. Cleared on the
    // next successful build, or when the slot is deleted.
    const [extractionWarning, setExtractionWarning] = useState(null);
    // shape: { ...detail, script_id } | null

    const fileRefs = useRef({});

    const refresh = async ({ surfaceError = true } = {}) => {
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/scripts`);
            setScripts(data || []);
            setError("");
            return data || [];
        } catch (e) {
            // Iter-140 — refresh failures during heavy load must NOT
            // contradict a success toast that already fired. Callers
            // that follow a successful mutation pass `surfaceError:
            // false`; the initial mount + polling still surface
            // errors normally so the page doesn't appear silently
            // broken when the user lands on it.
            if (surfaceError) {
                setError(formatApiError(e));
            }
            return null;
        }
    };

    useEffect(() => {
        (async () => {
            await refresh();
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // Iter-99 — poll while any slot is mid-indexing so the UI moves
    // out of "Indexing…" automatically once the BG extractor settles.
    useEffect(() => {
        const anyIndexing = (scripts || []).some(
            (s) => s.index_status === "indexing",
        );
        if (!anyIndexing) return;
        let aborted = false;
        const tick = async () => {
            if (aborted) return;
            // Iter-140 — Polling tick must NEVER surface "server busy"
            // banner after a successful upload. The next tick will
            // self-recover.
            const list = await refresh({ surfaceError: false });
            if (aborted) return;
            const stillIndexing = (list || []).some(
                (s) => s.index_status === "indexing",
            );
            if (stillIndexing) {
                setTimeout(tick, 1500);
            }
        };
        const t = setTimeout(tick, 1500);
        return () => {
            aborted = true;
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scripts]);

    // Iter-141 — Track a local indexing-start fallback for any
    // script that's currently indexing but lacks a server-stamped
    // timestamp (legacy upload). Clears the entry once the script
    // leaves the indexing state.
    useEffect(() => {
        setLocalIndexingStart((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const s of scripts || []) {
                if (s.index_status === "indexing") {
                    if (!s.indexing_started_at && !next[s.id]) {
                        next[s.id] = new Date().toISOString();
                        changed = true;
                    }
                } else if (next[s.id]) {
                    delete next[s.id];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [scripts]);

    const scriptStartedAt = (s) =>
        s?.indexing_started_at || localIndexingStart[s?.id] || null;

    // Iter-143 — write a single script row from a canonical server
    // payload. Used by every recovery action so the tile reflects
    // server truth verbatim; no fall-back to stale local values.
    const _applyScriptRow = (scriptId, payload) => {
        setScripts((prev) =>
            (prev || []).map((s) =>
                s.id === scriptId
                    ? {
                          ...s,
                          index_status: payload?.index_status ?? s.index_status,
                          index_error: payload?.index_error ?? null,
                          indexing_started_at:
                              payload?.indexing_started_at ?? null,
                          chars:
                              payload?.chars !== undefined
                                  ? payload.chars
                                  : s.chars,
                          page_count:
                              payload?.page_count !== undefined
                                  ? payload.page_count
                                  : s.page_count,
                      }
                    : s,
            ),
        );
    };

    const onRecoveryRefresh = async (script) => {
        setRecoveryBusyId({ id: script.id, kind: "refresh" });
        try {
            // Iter-143 — pull the canonical row for this script slot
            // from the server and apply it directly to local state.
            // Falls back to a full list refresh if the targeted lookup
            // fails — either way the tile always ends up reflecting
            // what the server says.
            const { data } = await api.get(`${apiBase}/${projectId}/scripts`);
            const row = (data || []).find((r) => r.id === script.id);
            if (row) {
                _applyScriptRow(script.id, row);
            } else {
                await refresh({ surfaceError: false });
            }
        } catch {
            // Last-ditch — never leave the tile stuck. A list refresh
            // re-syncs from server even on a transient blip.
            try {
                await refresh({ surfaceError: false });
            } catch {
                /* swallow */
            }
        } finally {
            setRecoveryBusyId(null);
        }
    };

    const onRecoveryRetry = async (script) => {
        setRecoveryBusyId({ id: script.id, kind: "retry" });
        try {
            // Iter-143 — reindex is synchronous (iter-142). Read the
            // final state from the response and apply it directly.
            // No more local-only timer reset — the server result is
            // authoritative.
            const { data } = await api.post(
                `${apiBase}/${projectId}/scripts/${script.id}/reindex`,
            );
            _applyScriptRow(script.id, data);
            const finalStatus = data?.index_status;
            if (finalStatus === "ready") {
                toast.success(`EP ${script.episode} — indexed`);
            } else if (finalStatus === "empty") {
                toast.info(`EP ${script.episode} — no text found`);
            } else if (finalStatus === "failed") {
                toast.error(
                    data?.index_error
                        ? `EP ${script.episode} — couldn't read the file: ${data.index_error}`
                        : `EP ${script.episode} — script didn't finish processing`,
                );
            } else {
                toast.info(`EP ${script.episode} — still processing`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
            // Even on error, refresh from server so the tile reflects
            // whatever the backend currently believes.
            try {
                await refresh({ surfaceError: false });
            } catch {
                /* swallow */
            }
        } finally {
            setRecoveryBusyId(null);
        }
    };

    // Iter-155B.4 — single source of truth for "does this Replace
    // need to be deliberately confirmed?". Mirrors ScriptSection —
    // covers has_original/filename AND parsed_at so we never silently
    // overwrite a built-from-paste episode either.
    const shouldConfirmReplace = (s) =>
        Boolean(s?.has_original || s?.filename || s?.parsed_at);

    const onRecoveryReplace = (script) => {
        // Route through the same Replace confirmation gate as the
        // per-episode Replace button. The user shouldn't get a
        // different (less deliberate) experience just because they
        // hit recovery.
        if (shouldConfirmReplace(script)) {
            setPendingReplaceIntent(script);
        } else {
            fileRefs.current[script.id]?.click();
        }
    };

    const submitNewSlot = async (e) => {
        e?.preventDefault?.();
        const ep = newEpisode.trim();
        if (!ep) {
            setNewError("Episode label is required (e.g. 1, Pilot, S2E04).");
            return;
        }
        if (scripts.some((s) => (s.episode || "").trim().toLowerCase() === ep.toLowerCase())) {
            setNewError("An episode with that label already exists.");
            return;
        }
        setSubmittingNew(true);
        setNewError("");
        try {
            await api.post(`${apiBase}/${projectId}/scripts`, {
                episode: ep,
                title: newTitle.trim() || null,
            });
            await refresh();
            setAddOpen(false);
            setNewEpisode("");
            setNewTitle("");
            toast.success(`EP ${ep} added`);
        } catch (err) {
            setNewError(formatApiError(err));
        } finally {
            setSubmittingNew(false);
        }
    };

    const onUpload = async (script, file) => {
        if (!file) return;
        // Iter-155B.3 — file picker now only opens AFTER the user
        // has confirmed Replace intent (handled by
        // onUploadButtonClick + ConfirmReplaceScriptDialog on the
        // pendingReplaceIntent path). By the time we receive a file
        // here we can commit immediately.
        await commitUpload(script, file);
        if (fileRefs.current[script.id]) fileRefs.current[script.id].value = "";
    };

    // Iter-155B.3 — per-episode upload button click handler. Gates on
    // confirmation when the slot already has a file OR has been built
    // (parsed_at) — see shouldConfirmReplace above.
    const onUploadButtonClick = (script) => {
        if (busyId === script.id) return;
        if (shouldConfirmReplace(script)) {
            setPendingReplaceIntent(script);
        } else {
            fileRefs.current[script.id]?.click();
        }
    };

    const confirmReplaceIntent = () => {
        const script = pendingReplaceIntent;
        setPendingReplaceIntent(null);
        if (script) fileRefs.current[script.id]?.click();
    };

    const commitUpload = async (script, file) => {
        if (!file || !script) return;
        setBusyId(script.id);
        try {
            const fd = new FormData();
            const { data: up } = await (async () => {
                fd.append("file", file);
                return api.post(
                    `${apiBase}/${projectId}/scripts/${script.id}/upload`,
                    fd,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 120000,
                    },
                );
            })();
            // Iter-143 — Trust the server response. The backend
            // (iter-142) returns the FINAL index_status synchronously;
            // we MUST use it verbatim and never fall back to the
            // prior local value, otherwise a stale `indexing` survives
            // a successful upload (the live deploy bug). Optimistic
            // updates are gone — server is the single source of truth.
            const serverStatus = up?.index_status ?? "ready";
            setScripts((prev) =>
                (prev || []).map((s) =>
                    s.id === script.id
                        ? {
                              ...s,
                              has_original: true,
                              filename: up?.filename || file.name,
                              size: up?.size ?? s.size,
                              chars: up?.chars ?? s.chars,
                              page_count: up?.page_count ?? s.page_count,
                              index_status: serverStatus,
                              index_error: up?.index_error ?? null,
                              indexing_started_at:
                                  up?.indexing_started_at ?? null,
                          }
                        : s,
                ),
            );
            if (serverStatus === "indexing") {
                // Should only happen for legacy (pre-iter-142) docs.
                toast.info(`Saved ${file.name} · still processing in background`);
            } else if (serverStatus === "failed") {
                toast.error(
                    up?.index_error
                        ? `We couldn't finish reading the script: ${up.index_error}`
                        : `Saved ${file.name}, but we couldn't finish reading it. You can try again from the script tile.`,
                );
            } else if (serverStatus === "empty") {
                toast.info(`Saved ${file.name} · no text found in the file`);
            } else {
                toast.success(`Uploaded ${file.name}`);
            }
            // Background canonical refresh — never surfaces an error
            // banner after a success. Polling effect picks up any
            // delta on the next tick anyway.
            await refresh({ surfaceError: false });
        } catch (err) {
            // Iter-145 — Production WiFi RCA. The upload POST can be
            // aborted by an intermediate (Cloudflare / k8s ingress
            // response-timeout) BEFORE the backend finishes writing
            // the file. A single follow-up GET (iter-144) fired too
            // soon — the slot was still empty at the moment we
            // checked, so the catch fell through to "Script upload
            // failed" even though the BE was still about to settle
            // the slot moments later.
            //
            // New strategy: POLL the slot for up to 30s waiting for
            // the BE to finish. Suppress any failure toast during
            // that window. Only declare a real failure if the slot
            // is still empty after the poll expires.
            const MAX_ATTEMPTS = 60; // 60 × 1s = 60s. Iter-155B.4 — doubled the recovery window so genuinely-slow proxies never produce a false-failure toast.
            const POLL_MS = 1000;
            let recovered = false;
            let recoveredRow = null;
            // Give the user one calm signal that we're still working
            // rather than letting the spinner stand alone for 60s.
            // Iter-157G P1 — replace the legacy "Still working on it,
            // please try again in a moment" wording that read as a
            // FAILURE prompting the user to retry. The work IS
            // succeeding; the message just needs to say so.
            const checkingToastId = toast.loading(
                `Revision uploaded · processing ${file.name} — review will be available shortly…`,
            );
            for (let attempt = 0; attempt < MAX_ATTEMPTS && !recovered; attempt++) {
                try {
                    const data = await refresh({ surfaceError: false });
                    const fresh = (data || []).find((s) => s.id === script.id);
                    if (
                        fresh?.has_original &&
                        fresh?.index_status &&
                        fresh.index_status !== "indexing"
                    ) {
                        recovered = true;
                        recoveredRow = fresh;
                        break;
                    }
                } catch {
                    /* swallow — best-effort recovery, keep polling */
                }
                if (!recovered) {
                    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
                }
            }
            toast.dismiss(checkingToastId);
            if (recovered && recoveredRow) {
                const recoveredStatus = recoveredRow.index_status;
                if (recoveredStatus === "ready") {
                    toast.success(`Uploaded ${file.name}`);
                } else if (recoveredStatus === "empty") {
                    toast.info(
                        `Saved ${file.name} · no text found in the file`,
                    );
                } else if (recoveredStatus === "failed") {
                    toast.error(
                        recoveredRow.index_error
                            ? `We couldn't finish reading the script: ${recoveredRow.index_error}`
                            : `Saved ${file.name}, but we couldn't finish reading it.`,
                    );
                }
            } else {
                // Iter-155B.4 — calm forward-pointing message instead
                // of alarming "upload failed" toast. The upload often
                // settles on the server side after our 60s recovery
                // window expires.
                toast.info(
                    "Upload is taking longer than usual. Refresh in a moment — your file may have made it through.",
                    { duration: 8000 },
                );
            }
        } finally {
            setBusyId(null);
            setPendingReplace(null);
            if (fileRefs.current[script.id]) fileRefs.current[script.id].value = "";
        }
    };

    const _runParse = async (script) => {
        // Iter-157P.6 — Operation Pineapple workflow trust fix.
        // Episodic "Build Scenes & Cast" used to call ONLY
        // `/scripts/{id}/parse`, leaving the operational
        // scene/cast state pointing at the previous confirmed
        // scenes. We now chain `/scenes/confirm` so producers see
        // the new state immediately — same fix as the single-script
        // path in ScriptSection.jsx. See that file's _runParse for
        // the full rationale.
        setBusyId(script.id);
        // Iter-157P.19 — clear any prior extraction warning when a
        // new build starts. If this run also fails the catch block
        // will re-set it; on success the warning stays cleared.
        if (extractionWarning?.script_id === script.id) {
            setExtractionWarning(null);
        }
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/scripts/${script.id}/parse`,
            );
            // Promote freshly-parsed drafts to confirmed for this
            // episode immediately. `/scenes/confirm` is episode-
            // scoped automatically (it inspects which episodes have
            // drafts).
            let confirmData = null;
            try {
                const r = await api.post(`${apiBase}/${projectId}/scenes/confirm`);
                confirmData = r.data;
            } catch (confirmErr) {
                console.warn("auto-confirm failed (episodic)", confirmErr);
                toast.message(
                    `EP ${script.episode}: scenes drafted — confirmation needs your attention`,
                    {
                        description:
                            "Open Scenes Review to finalize the new scenes list. Your continuity is safe.",
                    },
                );
                return;
            }
            const parts = [`${data.scenes_drafted || 0} scenes built`];
            if (data.characters_resolved) parts.push(`${data.characters_resolved} characters`);
            toast.success(`EP ${script.episode}: ${parts.join(" · ")}`);
            // Iter-147 — apply the persistent build stamp locally so
            // the tile shows the "Scenes & Cast built" subline
            // immediately without an extra refresh round-trip.
            setScripts((prev) =>
                (prev || []).map((s) =>
                    s.id === script.id
                        ? {
                              ...s,
                              parsed_at: data.parsed_at || new Date().toISOString(),
                              parsed_scenes_count:
                                  data.parsed_scenes_count ?? data.scenes_drafted ?? null,
                              parsed_characters_count:
                                  data.parsed_characters_count ?? data.characters_resolved ?? null,
                          }
                        : s,
                ),
            );
            // Iter-157P.6 — Broadcast scenes-changed so open Scene /
            // Cast views refetch without a manual reload.
            try {
                window.dispatchEvent(
                    new CustomEvent("osf:scenes-changed", {
                        detail: {
                            projectId,
                            episode: script.episode,
                            source: "build_scenes_and_cast",
                        },
                    }),
                );
                window.dispatchEvent(
                    new CustomEvent("osf:scripts-changed", {
                        detail: {
                            projectId,
                            episode: script.episode,
                            source: "build_scenes_and_cast",
                        },
                    }),
                );
            } catch (_) {
                /* no-op */
            }
            // Iter-149 (P2.5) — DO NOT auto-navigate to Scenes Review.
            // User stays in the setup context where they triggered
            // the rebuild. If any items landed in Needs Review they
            // can jump to Cast via the inline link below.
            if ((data.possible_characters_count || 0) > 0) {
                toast.message("Some items need review", {
                    description: `${data.possible_characters_count} character${data.possible_characters_count === 1 ? "" : "s"} in Needs Review on the Cast page.`,
                    action: {
                        label: "Review",
                        onClick: () =>
                            navigate(
                                `${routeBase}/${projectId}/cast`,
                                { state: { openNeedsReview: true } },
                            ),
                    },
                });
            }
            // Iter-150 — continuity preservation toast. Surfaced as
            // calm informational message, NEVER as an error. Nothing
            // was deleted; some items just need manual reassignment.
            const needsRe =
                (confirmData?.continuity_needs_reassignment || 0) +
                (data.continuity_needs_reassignment || 0);
            if (needsRe > 0) {
                toast.message(
                    `${needsRe} continuity item${needsRe === 1 ? "" : "s"} need reassignment`,
                    {
                        description:
                            "Nothing was deleted — these items were detached during the rebuild and are kept safe.",
                    },
                );
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                toast.error("Script is still being read — try again in a few seconds.");
            } else if (status === 422) {
                // Iter-157P.15 — structured zero-scene failure
                // surfaces as a calm, producer-friendly toast.
                // Previously we passed `err.response.data.detail`
                // (now an object) straight into `toast.error`,
                // which rendered "[object Object]" and could crash
                // sonner's React node path. Route through
                // formatApiError so we always emit a string, and
                // give the structured payload its own descriptive
                // toast with recovery copy matching the base /
                // PrepMode warning blocks.
                const detail = err?.response?.data?.detail;
                if (
                    detail &&
                    typeof detail === "object" &&
                    detail.error === "script_yielded_zero_scenes"
                ) {
                    // Iter-157P.19 — promote the zero-scene failure
                    // from a self-dismissing toast to a persistent
                    // panel rendered above the episode list. Same
                    // calm copy + recovery action as the base-script
                    // and PrepMode warnings.
                    setExtractionWarning({
                        ...detail,
                        script_id: script.id,
                    });
                } else {
                    toast.error(formatApiError(err));
                }
            } else {
                toast.error("We couldn't process the script. Please try again or upload a different file.");
            }
        } finally {
            setBusyId(null);
            setPendingRebuild(null);
        }
    };

    const onParse = (script) => {
        // Iter-147 — gate re-parses on a previously built slot through
        // a confirmation dialog so the user explicitly accepts the
        // scene-draft replacement.
        if (script?.parsed_at) {
            setPendingRebuild({ script });
            return;
        }
        return _runParse(script);
    };

    const onDelete = async (script) => {
        setBusyId(script.id);
        try {
            await api.delete(`${apiBase}/${projectId}/scripts/${script.id}`);
            await refresh();
            // Iter-157P.19 — drop any extraction warning that
            // pointed at this slot; the warning would otherwise
            // dangle after the slot is gone.
            if (extractionWarning?.script_id === script.id) {
                setExtractionWarning(null);
            }
            // Iter-157H P0(3) — Script deletion ghost state. Broadcast
            // so other surfaces (Project Home tile, Cast Hub, Scenes
            // Hub, EmptyScriptOnboarding) refetch and drop the
            // deleted script from their cached state immediately.
            try {
                window.dispatchEvent(
                    new CustomEvent("osf:scripts-changed", {
                        detail: { project_id: projectId, action: "deleted", script_id: script.id },
                    }),
                );
            } catch { /* no-op */ }
            toast.success(`EP ${script.episode} deleted`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
            setConfirmDeleteId(null);
        }
    };

    return (
        <div data-testid="episodic-script-setup" className="space-y-3">
            {error ? (
                <p data-testid="episodic-script-error" className="text-sm text-red-400">
                    {error}
                </p>
            ) : null}

            {extractionWarning ? (() => {
                // Iter-157P.19 — persistent zero-scene panel for
                // the episodic build path. Mirrors the
                // ScriptSection / PrepMode copy + light-mode-safe
                // colour ramp. Sits above the episode list so the
                // producer can re-read the recovery action while
                // they re-export and re-upload.
                const w = extractionWarning;
                const active = w.active_format;
                const epLabel = (() => {
                    const sid = w.script_id;
                    if (!sid) return null;
                    const row = (scripts || []).find((s) => s.id === sid);
                    return row?.episode ? `EP ${row.episode}` : null;
                })();
                const headline = active === "DOCX"
                    ? "We couldn\u2019t identify any scenes in this DOCX."
                    : active === "PDF"
                        ? "We couldn\u2019t identify any scenes in this PDF."
                        : "We couldn\u2019t identify any scenes in this script.";
                const explanation = active === "DOCX"
                    ? "This DOCX may have been exported in a format that doesn\u2019t preserve readable scene headings."
                    : "This PDF may have been exported in a format that doesn\u2019t preserve readable text.";
                const recommendation = active === "DOCX"
                    ? "Try re-exporting the DOCX from your screenwriting tool, or upload the PDF version of the script."
                    : active === "PDF"
                        ? "Try exporting the PDF using \u201CBest for electronic distribution and accessibility\u201D, or upload the DOCX version of the script."
                        : "Try exporting the PDF using \u201CBest for electronic distribution and accessibility\u201D, or upload the DOCX version of the script.";
                return (
                    <div
                        data-testid="episodic-script-extraction-warning"
                        role="alert"
                        aria-live="polite"
                        className="rounded-xl border border-red-500/25 bg-red-500/10 p-4 space-y-2"
                    >
                        <p className="text-sm text-red-300 font-semibold leading-snug">
                            {epLabel ? `${epLabel} — ${headline}` : headline}
                        </p>
                        <p className="text-[13px] text-red-200 leading-relaxed">
                            {explanation}
                        </p>
                        <p className="text-[13px] text-red-200 leading-relaxed">
                            <span className="font-semibold">Try this:</span>{" "}
                            {recommendation}
                        </p>
                        <p className="text-[13px] text-emerald-300 leading-relaxed">
                            Your project is safe. No changes have been made.
                        </p>
                        <div className="pt-1">
                            <button
                                type="button"
                                data-testid="episodic-script-extraction-warning-dismiss"
                                onClick={() => setExtractionWarning(null)}
                                className="tap inline-flex items-center h-7 px-3 rounded-full text-[11px] uppercase tracking-[0.2em] text-red-200 border border-red-500/30 hover:bg-red-500/15"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                );
            })() : null}

            {loading ? (
                <div className="h-24 rounded-xl border border-white/10 bg-card/40 animate-pulse" />
            ) : scripts.length === 0 ? (
                <div
                    data-testid="episodic-script-empty"
                    className="rounded-xl border border-dashed border-white/10 p-6 text-center"
                >
                    <FileText size={20} className="mx-auto text-neutral-500" />
                    <p className="mt-2 text-sm text-neutral-300">No episodes yet.</p>
                    <p className="text-xs text-neutral-500 mt-1">
                        Add an episode below, upload its script, and we&apos;ll build the scenes &amp; cast.
                    </p>
                </div>
            ) : (
                <ul data-testid="episodic-script-list" className="space-y-2">
                    {scripts.map((s) => (
                        <li
                            key={s.id}
                            data-testid={`episodic-script-row-${s.id}`}
                            className="rounded-xl border border-white/10 bg-card p-4"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            data-testid={`episodic-script-episode-${s.id}`}
                                            className="inline-flex items-center justify-center h-6 px-2 rounded-full bg-primary/15 text-primary text-[11px] font-bold tracking-wide"
                                        >
                                            EP {s.episode || "—"}
                                        </span>
                                        {s.has_original ? (
                                            s.index_status === "indexing" ? (
                                                <span
                                                    data-testid={`episodic-script-index-indexing-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-violet-200"
                                                >
                                                    <Loader2 size={10} className="animate-spin" /> Reading
                                                </span>
                                            ) : s.index_status === "failed" ? (
                                                <span
                                                    data-testid={`episodic-script-index-failed-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-amber-300"
                                                >
                                                    <AlertTriangle size={10} /> Read failed
                                                </span>
                                            ) : s.index_status === "empty" ? (
                                                <span
                                                    data-testid={`episodic-script-index-empty-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-amber-300"
                                                >
                                                    No text
                                                </span>
                                            ) : (
                                                <span
                                                    data-testid={`episodic-script-index-ready-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-300"
                                                >
                                                    <CheckCircle2 size={10} /> Ready
                                                </span>
                                            )
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                                Empty
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 text-white font-medium truncate">
                                        {s.title || s.filename || `EP ${s.episode}`}
                                    </div>
                                    {s.filename ? (
                                        <div className="text-[11px] text-neutral-500 truncate">
                                            {s.filename}
                                        </div>
                                    ) : null}
                                </div>
                                <button
                                    type="button"
                                    data-testid={`episodic-script-delete-${s.id}`}
                                    onClick={() => setConfirmDeleteId(s.id)}
                                    disabled={busyId === s.id}
                                    aria-label="Delete episode"
                                    className="tap h-9 w-9 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <input
                                    ref={(el) => {
                                        if (el) fileRefs.current[s.id] = el;
                                    }}
                                    type="file"
                                    data-testid={`episodic-script-file-input-${s.id}`}
                                    accept=".pdf,.docx,.txt,.fountain"
                                    className="hidden"
                                    onChange={(e) =>
                                        onUpload(s, e.target.files?.[0])
                                    }
                                />
                                <Button
                                    type="button"
                                    data-testid={`episodic-script-upload-${s.id}`}
                                    variant="secondary"
                                    disabled={busyId === s.id}
                                    onClick={() => onUploadButtonClick(s)}
                                    className="min-tap"
                                >
                                    <Upload size={14} className="mr-1.5" />
                                    {busyId === s.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : s.has_original ? (
                                        "Replace"
                                    ) : (
                                        "Upload"
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    data-testid={`episodic-script-parse-${s.id}`}
                                    disabled={
                                        !s.has_original ||
                                        busyId === s.id ||
                                        s.index_status === "indexing" ||
                                        s.index_status === "failed" ||
                                        s.index_status === "empty"
                                    }
                                    onClick={() => onParse(s)}
                                    className={`min-tap font-semibold ${
                                        // Iter-155B.1 — same layperson
                                        // guidance pattern: gently
                                        // highlight Build until it's
                                        // been pressed for this episode.
                                        s.has_original &&
                                        !s.parsed_at &&
                                        busyId !== s.id &&
                                        s.index_status !== "indexing"
                                            ? "ring-2 ring-primary/40 ring-offset-2 ring-offset-background"
                                            : ""
                                    }`}
                                >
                                    <Wand2 size={14} className="mr-1.5" />
                                    {busyId === s.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : s.index_status === "indexing" ? (
                                        "Reading…"
                                    ) : s.parsed_at ? (
                                        "Build again"
                                    ) : (
                                        "Build Scenes & Cast"
                                    )}
                                </Button>
                            </div>

                            {s.has_original && s.index_status === "indexing" ? (
                                <ScriptIndexingRecovery
                                    startedAt={scriptStartedAt(s)}
                                    onRefresh={() => onRecoveryRefresh(s)}
                                    onRetry={() => onRecoveryRetry(s)}
                                    onReplace={() => onRecoveryReplace(s)}
                                    busy={
                                        recoveryBusyId?.id === s.id
                                            ? recoveryBusyId.kind
                                            : null
                                    }
                                    testidPrefix={`episodic-script-recovery-${s.id}`}
                                />
                            ) : null}

                            <ScriptParsedBadge
                                parsedAt={s.parsed_at}
                                scenesCount={s.parsed_scenes_count}
                                charactersCount={s.parsed_characters_count}
                                testid={`episodic-script-parsed-badge-${s.id}`}
                            />

                            {/* Iter-158.13.2.20b — Route 2 parity
                                restored. Panel now renders on both
                                mounts via its existing apiBase
                                handling. */}
                            {s.has_original ? (
                                <ScriptRevisionPanel
                                    projectId={projectId}
                                    scriptId={s.id}
                                />
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}

            <Button
                type="button"
                data-testid="episodic-script-add-open"
                onClick={() => {
                    setNewEpisode("");
                    setNewTitle("");
                    setNewError("");
                    setAddOpen(true);
                }}
                variant="secondary"
                className="w-full min-tap"
            >
                <Plus size={14} className="mr-1.5" /> Add episode
            </Button>

            <Dialog
                open={addOpen}
                onOpenChange={(v) => !submittingNew && setAddOpen(v)}
            >
                <DialogContent
                    data-testid="episodic-script-add-dialog"
                    className="max-w-md bg-card border-white/10"
                >
                    <DialogHeader className="pr-8">
                        <DialogTitle className="font-display text-xl font-bold text-white">
                            Add episode
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            Use the same episode label your team uses on set
                            (e.g. <span className="text-white">1</span>,{" "}
                            <span className="text-white">Pilot</span>,{" "}
                            <span className="text-white">S2E04</span>). Scene
                            numbers are scoped per episode, so two episodes
                            can both have a Scene 1.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitNewSlot} className="space-y-3 mt-4">
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="episodic-add-episode"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Episode label
                            </Label>
                            <Input
                                id="episodic-add-episode"
                                data-testid="episodic-script-add-episode-input"
                                autoFocus
                                value={newEpisode}
                                onChange={(e) => {
                                    setNewEpisode(e.target.value);
                                    if (newError) setNewError("");
                                }}
                                placeholder="1"
                                maxLength={20}
                                className="h-12 bg-secondary border-transparent text-base"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="episodic-add-title"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Title (optional)
                            </Label>
                            <Input
                                id="episodic-add-title"
                                data-testid="episodic-script-add-title-input"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                placeholder="EP 1 — Pilot"
                                maxLength={120}
                                className="h-12 bg-secondary border-transparent text-base"
                            />
                        </div>
                        {newError ? (
                            <p
                                data-testid="episodic-script-add-error"
                                className="text-sm text-red-400"
                            >
                                {newError}
                            </p>
                        ) : null}
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                data-testid="episodic-script-add-cancel"
                                variant="secondary"
                                onClick={() => setAddOpen(false)}
                                disabled={submittingNew}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                data-testid="episodic-script-add-submit"
                                disabled={submittingNew || !newEpisode.trim()}
                                className="flex-1 min-tap font-semibold"
                            >
                                {submittingNew ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    "Add"
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog
                open={Boolean(confirmDeleteId)}
                onOpenChange={(v) => !v && setConfirmDeleteId(null)}
            >                <DialogContent
                    data-testid="episodic-script-delete-dialog"
                    className="max-w-md bg-card border-red-500/30"
                >
                    <DialogHeader className="pr-8">
                        <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <AlertTriangle size={18} className="text-red-300" />
                            Delete this episode?
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            This removes the script slot only. Scenes and
                            cast already confirmed for this episode are{" "}
                            <strong className="text-white">not</strong>{" "}
                            deleted — re-upload a script to replace it later.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-2 pt-4">
                        <Button
                            type="button"
                            data-testid="episodic-script-delete-cancel"
                            variant="secondary"
                            onClick={() => setConfirmDeleteId(null)}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="episodic-script-delete-confirm"
                            variant="destructive"
                            onClick={() => {
                                const target = scripts.find(
                                    (s) => s.id === confirmDeleteId,
                                );
                                if (target) onDelete(target);
                            }}
                            disabled={busyId === confirmDeleteId}
                            className="flex-1 min-tap"
                        >
                            {busyId === confirmDeleteId ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <>
                                    <Trash2 size={14} className="mr-1.5" />
                                    Delete
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <ConfirmReplaceScriptDialog
                open={Boolean(pendingReplace) || Boolean(pendingReplaceIntent)}
                onOpenChange={(v) => {
                    if (!v) {
                        setPendingReplace(null);
                        setPendingReplaceIntent(null);
                    }
                }}
                currentFilename={
                    pendingReplace?.script?.filename ||
                    pendingReplaceIntent?.filename
                }
                onConfirm={() => {
                    if (pendingReplace) {
                        commitUpload(pendingReplace.script, pendingReplace.file);
                    } else if (pendingReplaceIntent) {
                        confirmReplaceIntent();
                    }
                }}
                busy={busyId === pendingReplace?.script?.id}
            />

            <ConfirmRebuildSceneCastDialog
                open={Boolean(pendingRebuild)}
                onOpenChange={(v) => {
                    if (!v) setPendingRebuild(null);
                }}
                episodeLabel={pendingRebuild?.script?.episode}
                scenesCount={pendingRebuild?.script?.parsed_scenes_count}
                charactersCount={pendingRebuild?.script?.parsed_characters_count}
                onConfirm={() => {
                    if (pendingRebuild?.script) {
                        _runParse(pendingRebuild.script);
                    }
                }}
                busy={busyId === pendingRebuild?.script?.id}
            />
        </div>
    );
}
